// User Preferences Management
// إدارة تفضيلات المستخدم

const STORAGE_KEYS = {
  LANGUAGE: 'realmap-language',
  MAP_STYLE: 'realmap-map-style',
  SIDEBAR_POSITION: 'realmap-sidebar-position',
  SELECTED_PROJECT: 'realmap_selectedProject',
  // يمكن إضافة المزيد من التفضيلات هنا
};

const DEFAULT_VALUES = {
  LANGUAGE: 'ar',
  MAP_STYLE: 'satellite-streets-v12',
  SIDEBAR_POSITION: 'auto', // 'left', 'right', 'auto' (auto = based on language)
};

/**
 * Get user's language preference
 * الحصول على تفضيل اللغة للمستخدم
 */
export const getLanguagePreference = () => {
  try {
    const savedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    // تحقق من أن اللغة المحفوظة صالحة
    if (savedLanguage && (savedLanguage === 'ar' || savedLanguage === 'en')) {
      return savedLanguage;
    }
    return DEFAULT_VALUES.LANGUAGE;
  } catch (error) {
    return DEFAULT_VALUES.LANGUAGE;
  }
};

/**
 * Save user's language preference
 * حفظ تفضيل اللغة للمستخدم
 */
export const setLanguagePreference = (language) => {
  try {
    if (language === 'ar' || language === 'en') {
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

/**
 * Get user's map style preference
 * الحصول على تفضيل نمط الخريطة للمستخدم
 */
export const getMapStylePreference = () => {
  try {
    const savedMapStyle = localStorage.getItem(STORAGE_KEYS.MAP_STYLE);
    return savedMapStyle || DEFAULT_VALUES.MAP_STYLE;
  } catch (error) {
    console.warn('Error reading map style preference from localStorage:', error);
    return DEFAULT_VALUES.MAP_STYLE;
  }
};

/**
 * Save user's map style preference
 * حفظ تفضيل نمط الخريطة للمستخدم
 */
export const setMapStylePreference = (mapStyle) => {
  try {
    localStorage.setItem(STORAGE_KEYS.MAP_STYLE, mapStyle);
    return true;
  } catch (error) {
    console.error('Error saving map style preference to localStorage:', error);
    return false;
  }
};

/**
 * Get user's sidebar position preference
 * الحصول على تفضيل موقع القائمة الجانبية للمستخدم
 */
export const getSidebarPositionPreference = () => {
  try {
    const savedPosition = localStorage.getItem(STORAGE_KEYS.SIDEBAR_POSITION);
    // تحقق من أن الموقع المحفوظ صالح
    if (savedPosition && ['left', 'right', 'auto'].includes(savedPosition)) {
      return savedPosition;
    }
    return DEFAULT_VALUES.SIDEBAR_POSITION;
  } catch (error) {
    console.warn('Error reading sidebar position preference from localStorage:', error);
    return DEFAULT_VALUES.SIDEBAR_POSITION;
  }
};

/**
 * Save user's sidebar position preference
 * حفظ تفضيل موقع القائمة الجانبية للمستخدم
 */
export const setSidebarPositionPreference = (position) => {
  try {
    if (['left', 'right', 'auto'].includes(position)) {
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_POSITION, position);
      return true;
    } else {
      console.warn('Invalid sidebar position preference:', position);
      return false;
    }
  } catch (error) {
    console.error('Error saving sidebar position preference to localStorage:', error);
    return false;
  }
};

/**
 * Get actual sidebar position based on preference and language
 * الحصول على موقع القائمة الجانبية الفعلي بناءً على التفضيل واللغة
 */
export const getActualSidebarPosition = (language, preference = null) => {
  const sidebarPreference = preference || getSidebarPositionPreference();

  if (sidebarPreference === 'auto') {
    // Auto mode: use language-based positioning
    return language === 'ar' ? 'right' : 'left';
  }

  // Use explicit preference
  return sidebarPreference;
};

/**
 * Clear stored selected project to prevent auto-navigation
 * مسح المشروع المحفوظ لمنع التنقل التلقائي
 */
export const clearSelectedProject = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.SELECTED_PROJECT);
    return true;
  } catch (error) {
    console.error('Error clearing selected project from localStorage:', error);
    return false;
  }
};

/**
 * Clear all user preferences
 * مسح جميع تفضيلات المستخدم
 */
export const clearAllPreferences = () => {
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    return true;
  } catch (error) {
    console.error('Error clearing preferences from localStorage:', error);
    return false;
  }
};

/**
 * Check if localStorage is available
 * التحقق من توفر localStorage
 */
export const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (error) {
    return false;
  }
}; 